import React, { useEffect } from "react";
import ReactMarkdown from "react-markdown";
import { theme as codeTheme } from "../CodeTab/outputTheme";
import Highlight, { defaultProps, Language } from "prism-react-renderer";
import styled from "styled-components";
import { DisclaimerContent } from "./DisclaimerContent";
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

// You can move these styled components into a separate file if you prefer
export const UserMessage = styled.div`
  background-color: #f0f8ff;
  border-radius: 16px;
  padding: 6px 10px;
  margin-bottom: 4px;
  box-shadow: -1px 0px 10px 1px rgba(0, 0, 0, 0.08);
  align-self: flex-end;
  margin-right: 10px;
  margin-left: auto;
  display: inline-block;
  width: auto;
  max-width: 80%;
  text-align: left;
  font-size: 1.2em;
  white-space: pre-wrap;
  word-wrap: break-word;
`;

export const AssistantMessage = styled.div`
  max-width: 95%;
  align-self: flex-start;
  margin-bottom: 8px;
`;

type ChatMessage = {
  role: "user" | "assistant";
  content: string;
  includedFiles?: { name: string; content?: string }[];
  excludedFiles?: string[];
};

interface ChatHistoryProps {
  chatHistory: ChatMessage[];
}

export const ChatHistory: React.FC<ChatHistoryProps> = ({ chatHistory }) => {
  return (
    <div style={{ display: "flex", flexDirection: "column" }}>
      {chatHistory.map((message, index) => {
        const isUser = message.role === "user";
        const isAssistant = message.role === "assistant";
        const MessageComponent = isUser ? UserMessage : AssistantMessage;

        // Determine if this is the most recent assistant message
        const isMostRecentAssistant =
          isAssistant && index === chatHistory.length - 1;

        return (
          <div
            key={index}
            data-chat-index={index}
            style={{
              marginBottom: "12px",
              textAlign: isUser ? "right" : "left",
            }}
          >
            {isAssistant && !isMostRecentAssistant ? (
              <Accordion square elevation={0} defaultExpanded={false}>
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  aria-controls={`panel-content-${index}`}
                  id={`panel-header-${index}`}
                >
                  <Typography variant="subtitle2" color="text.secondary">
                    Previous AI Response
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <MessageComponent>
                    <ReactMarkdown
                      components={{
                        code(props) {
                          const { children, className, ...rest } = props;
                          const match = /language-(\w+)/.exec(className || "");
                          return match ? (
                            <div
                              style={{
                                overflowX: "auto",
                                width: "100%",
                                backgroundColor: "#f6f8fa",
                                margin: 4,
                                boxShadow: "-1px 0px 10px 1px rgba(0,0,0,0.08)",
                              }}
                            >
                              <Highlight
                                {...defaultProps}
                                code={String(children).replace(/\n$/, "")}
                                language={match[1] as Language}
                                theme={codeTheme}
                              >
                                {({
                                  className: appliedName,
                                  style,
                                  tokens,
                                  getLineProps,
                                  getTokenProps,
                                }) => (
                                  <pre
                                    className={appliedName}
                                    style={{
                                      ...style,
                                      padding: "16px",
                                      width: "100%",
                                    }}
                                  >
                                    {tokens.map((line, i) => (
                                      <div
                                        {...getLineProps({ line, key: i })}
                                        key={i}
                                      >
                                        {line.map((token, key) => (
                                          <span
                                            {...getTokenProps({ token, key })}
                                            key={key}
                                          />
                                        ))}
                                      </div>
                                    ))}
                                  </pre>
                                )}
                              </Highlight>
                            </div>
                          ) : (
                            <code {...rest} className={className}>
                              {children}
                            </code>
                          );
                        },
                      }}
                    >
                      {message.content}
                    </ReactMarkdown>
                  </MessageComponent>
                </AccordionDetails>
              </Accordion>
            ) : (
              // Render user messages and the latest assistant message normally
              <>
                <MessageComponent>
                  <ReactMarkdown
                    components={{
                      code(props) {
                        const { children, className, ...rest } = props;
                        const match = /language-(\w+)/.exec(className || "");
                        return match ? (
                          <div
                            style={{
                              overflowX: "auto",
                              width: "100%",
                              backgroundColor: "#f6f8fa",
                              margin: 4,
                              boxShadow: "-1px 0px 10px 1px rgba(0,0,0,0.08)",
                            }}
                          >
                            <Highlight
                              {...defaultProps}
                              code={String(children).replace(/\n$/, "")}
                              language={match[1] as Language}
                              theme={codeTheme}
                            >
                              {({
                                className: appliedName,
                                style,
                                tokens,
                                getLineProps,
                                getTokenProps,
                              }) => (
                                <pre
                                  className={appliedName}
                                  style={{
                                    ...style,
                                    padding: "16px",
                                    width: "100%",
                                  }}
                                >
                                  {tokens.map((line, i) => (
                                    <div
                                      {...getLineProps({ line, key: i })}
                                      key={i}
                                    >
                                      {line.map((token, key) => (
                                        <span
                                          {...getTokenProps({ token, key })}
                                          key={key}
                                        />
                                      ))}
                                    </div>
                                  ))}
                                </pre>
                              )}
                            </Highlight>
                          </div>
                        ) : (
                          <code {...rest} className={className}>
                            {children}
                          </code>
                        );
                      },
                    }}
                  >
                    {message.content}
                  </ReactMarkdown>
                </MessageComponent>

                {/* Show disclaimers if it's a user message */}
                {isUser && (
                  <DisclaimerContent
                    includedFiles={message.includedFiles}
                    excludedFiles={message.excludedFiles}
                  />
                )}
              </>
            )}
          </div>
        );
      })}
    </div>
  );
};

// ------- SCROLL BEHAVIOR EFFECT -------

// The effect must be *outside* the component's return but *inside* the component body.

export const useChatScrollEffect = (chatHistory: ChatMessage[]) => {
  useEffect(() => {
    if (!chatHistory || chatHistory.length === 0) return;

    // We only adjust scroll after an assistant message renders (either placeholder or final response).
    const lastMsg = chatHistory[chatHistory.length - 1];
    if (lastMsg.role !== "assistant") return;

    const container = document.getElementById("chatContainer");
    if (!container) return;

    // Find the last user message index (search backwards).
    let lastUserIndex = -1;
    for (let i = chatHistory.length - 1; i >= 0; i--) {
      if (chatHistory[i].role === "user") {
        lastUserIndex = i;
        break;
      }
    }

    if (lastUserIndex === -1) return;

    const userElement = container.querySelector(
      `[data-chat-index="${lastUserIndex}"]`
    ) as HTMLElement | null;

    if (userElement) {
      container.scrollTop = userElement.offsetTop;
    }
  }, [chatHistory]);
};
